'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import sortBy from 'lodash/sortBy';
import {
  useState,
  useContext,
  ForwardRefExoticComponent,
  RefAttributes,
  useRef,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI lib components
import { Accordion, Box, Flex, Group, Stack, Text } from '@mantine/core';

// UI local components
import ContentLoader from '@ui-kit/molecules/ContentLoader';
import SectionInformation from './SectionInformation';
import SectionRedact from './SectionRedact';
import SectionWatermarks from './SectionWatermarks';
import SectionAnnotations from './SectionAnnotations';
import SectionPermissions from './SectionPermissions';
import SectionActivities from './SectionActivities';
import SectionSupport from './SectionSupport';
import SectionTranslation from './SectionTranslation';
import SectionMetadata from './SectionMetadata';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import { PageFileContext } from '@features/Resource/PageFile/context';

// Account
import useAccount from '@features/Account/store/useAccount';

// Local helpers & utils
import { isImage, isProcessableAsPdf, isSpreadsheet } from '@helpers/file';

// API & Types
import { Resource } from '@app-types/blackroom-api';
import { ActionRole } from '@app-types/blackroom-app';
import {
  ResourceMenuProps,
  ResourceMenuSection,
  SectionContentProps,
} from './types';

// Assets
import {
  IconEyeOff as IconRedact,
  IconMessages as IconAnnotations,
  IconLock as IconPermissions,
  IconReport as IconActivities,
  IconRubberStamp as IconWatermarks,
  IconProps,
  IconInfoCircle,
  IconMessageQuestion as IconSupport,
  IconMessageSearch as IconInsights,
  IconScale as IconMetadata,
  IconX as IconClose,
} from '@tabler/icons-react';

// Style
import { palette } from '@ui-kit/colors';
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import * as classes from './styles/index.css';
import * as accordionStylesAPI from './styles/accordionStylesAPI.css';
import SectionInsightsContent from './SectionInsights/SectionInsightsContent';
import ActionButtons from './ActionButtons';
import { FullscreenContainer } from './components';
import { useFullscreen } from './hooks';
import { isSpecialUserEmail } from '@shared/constants/users';

// Constants
const SectionDescriptor: Record<
  ResourceMenuSection,
  {
    iconComponent: ForwardRefExoticComponent<
      IconProps & RefAttributes<SVGSVGElement>
    >;
    sectionComponent: (props: SectionContentProps) => React.JSX.Element;
    rank?: number;
    allowedRoles?: ActionRole[];
  }
> = {
  metadata: {
    sectionComponent: SectionMetadata,
    iconComponent: IconMetadata,
    rank: 0,
    allowedRoles: ['workspace-admin'],
  },
  redact: {
    sectionComponent: SectionRedact,
    iconComponent: IconRedact,
    rank: 1,
    allowedRoles: ['workspace-admin'],
  },
  watermarks: {
    sectionComponent: SectionWatermarks,
    iconComponent: IconWatermarks,
    rank: 2,
    allowedRoles: ['workspace-admin'],
  },
  annotations: {
    sectionComponent: SectionAnnotations,
    iconComponent: IconAnnotations,
    rank: 3,
  },
  permissions: {
    sectionComponent: SectionPermissions,
    iconComponent: IconPermissions,
    rank: 4,
    allowedRoles: ['workspace-admin'],
  },
  activities: {
    sectionComponent: SectionActivities,
    iconComponent: IconActivities,
    rank: 5,
  },
  support: {
    sectionComponent: SectionSupport,
    iconComponent: IconSupport,
    rank: 6,
    allowedRoles: ['user', 'workspace-admin', 'tenant-admin'],
  },
  translation: {
    sectionComponent: SectionTranslation,
    iconComponent: IconInsights,
    rank: 7,
    allowedRoles: ['user', 'workspace-admin', 'tenant-admin'],
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceMenu({
  allowedSections = ['activities'],
  withPreview = true,
  resource: fullResource,
  prefilledResource,
  loading = false,
  resetAfterDelete: onCloseMenu,
  ...props
}: ResourceMenuProps) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useWorkspace();
  const { isCurrentWorkspaceAdmin, account } = useAccount();
  const location = useLocation();
  const { t } = useTranslation('Resource');

  // State
  const userActionRole: ActionRole = isCurrentWorkspaceAdmin
    ? 'workspace-admin'
    : 'user';
  const isPageFileLocation = /(^|\/)file(\/|$)/.test(location.pathname);

  const [currentSection, setCurrentSection] =
    useState<ResourceMenuSection | null>(null);

  // Ref to track accordion items for scrolling
  const accordionItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Fullscreen management for insights section
  const insightsFullscreen = useFullscreen({
    initialState: false,
    autoExpandOnMessage: true,
  });

  // Context
  const pageFileContext = useContext(PageFileContext);

  // Use full resource when available, otherwise fall back to prefilled data for immediate display
  const resource = (fullResource ?? prefilledResource) as Resource | undefined;

  function renderInner() {
    if (!resource) {
      return (
        <Stack>
          <Flex justify="flex-end" p="md" c={palette.blackroomDarkGray1}>
            <IconClose
              size={ICON_SIZE_MAP.small}
              className={classes.closeMenu}
              onClick={onCloseMenu}
            />
          </Flex>
          <Stack align="center" pt={80} p="lg" gap="lg" w="100%">
            <IconInfoCircle color={palette.blackroomBlue} size={60} />
            <Text ta="center" c={palette.blackroomBlue}>
              {t('ResourceMenu.openMenuDisabled')}
            </Text>
          </Stack>
        </Stack>
      );
    }

    return (
      <div style={{ position: 'relative', height: '100%' }}>
        {!isPageFileLocation && (
          <Group
            justify="space-between"
            p="md"
            c={palette.blackroomDarkGray1}
            wrap="nowrap"
          >
            <Link
              to={`/workspace/${workspace.id}/${resource.type}/${resource.id}`}
              style={{ overflow: 'hidden', display: 'flex', gap: '0.3rem' }}
              target="_blank"
            >
              {resource.index && <Text size="md">{resource.index}</Text>}
              <Text truncate>{resource.name}</Text>
            </Link>
            <div>
              <IconClose
                size={ICON_SIZE_MAP.small}
                className={classes.closeMenu}
                onClick={onCloseMenu}
              />
            </div>
          </Group>
        )}

        <Box className={isPageFileLocation ? classes.pageFileActions : ''}>
          <ActionButtons resource={resource} closeMenu={onCloseMenu} />
        </Box>

        {resource.pspdfKitId && (
          <FullscreenContainer
            isFullscreen={insightsFullscreen.isFullscreen}
            radius="xs"
            p="xs"
            mih={0}
            shadow="xs"
            withBorder
            normalHeight="390px"
            normalMargin="1rem"
          >
            <SectionInsightsContent
              resource={resource}
              isFullscreen={insightsFullscreen.isFullscreen}
              onToggleFullscreen={insightsFullscreen.toggleFullscreen}
            />
          </FullscreenContainer>
        )}

        <Stack
          className={classes.resourceSection}
          pt={resource.pspdfKitId ? 0 : '1rem'}
          pb={0}
        >
          <SectionInformation resource={resource} />
        </Stack>

        <Box className={classes.accordionSection}>
          <Accordion
            classNames={{ ...accordionStylesAPI }}
            variant="contained"
            radius={0}
            chevron={null}
            defaultValue={currentSection}
            styles={{
              content: {
                width: '100%',
              },
            }}
            onChange={(section) => {
              setCurrentSection(section as ResourceMenuSection | null);
              // Scroll to the opened accordion item only if it's not visible
              if (section) {
                setTimeout(() => {
                  const element = accordionItemRefs.current[section];
                  if (element) {
                    const rect = element.getBoundingClientRect();
                    const isVisible =
                      rect.top >= 0 &&
                      rect.bottom <=
                        (window.innerHeight ||
                          document.documentElement.clientHeight);

                    // Only scroll if the element is not fully visible
                    if (!isVisible) {
                      element.scrollIntoView({
                        behavior: 'smooth',
                      });
                    }
                  }
                }, 100); // Small delay to allow accordion animation to start
              }
            }}
          >
            {sortBy(Object.entries(SectionDescriptor), ([, d]) => d.rank ?? 0)
              .filter(([section, descriptor]) => {
                if (
                  section === 'support' &&
                  import.meta.env.VITE_APP_SUPPORT !== 'true'
                ) {
                  return false;
                }
                if (!allowedSections.includes(section as ResourceMenuSection)) {
                  return false;
                }
                if (
                  descriptor.allowedRoles?.includes(userActionRole) === false
                ) {
                  return false;
                }
                if (
                  section === 'translation' &&
                  (!isProcessableAsPdf(resource) ||
                    isSpreadsheet(resource) ||
                    isImage(resource) ||
                    pageFileContext.viewRedactedFile)
                ) {
                  return false;
                }
                if (
                  section === 'metadata' &&
                  !isSpecialUserEmail(account?.email)
                ) {
                  return false;
                }
                if (
                  section === 'insights' &&
                  (!isProcessableAsPdf(resource) ||
                    isSpreadsheet(resource) ||
                    isImage(resource) ||
                    pageFileContext.viewRedactedFile ||
                    !resource.pspdfKitId)
                ) {
                  return false;
                }
                return true;
              })
              .map(([section]) => {
                const {
                  iconComponent: IconComponent,
                  sectionComponent: SectionComponent,
                } =
                  SectionDescriptor[section as keyof typeof SectionDescriptor];
                return (
                  <Accordion.Item
                    key={section}
                    value={section}
                    ref={(el) => {
                      accordionItemRefs.current[section] = el;
                    }}
                  >
                    <Accordion.Control
                      icon={<IconComponent size={ICON_SIZE_MAP.small} />}
                      my={0}
                    >
                      {t(`ResourceMenu.sections.${section}.title`)}
                    </Accordion.Control>
                    <Accordion.Panel p={0}>
                      <div style={{ paddingBottom: '1rem' }}>
                        <SectionComponent resource={resource} />
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
          </Accordion>
        </Box>
      </div>
    );
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Stack
      className={classes.resourceMenu}
      gap={0}
      justify="start"
      align="stretch"
      bg="blackroomWhite"
      w="100%"
    >
      {loading ? (
        <ContentLoader containerProps={{ mt: 'lg', px: 'xl' }} withCircle />
      ) : (
        renderInner()
      )}
    </Stack>
  );
}

export default ResourceMenu;
